import React, { ReactNode, useEffect, useState } from 'react';

import { formatDateString } from '~libs/dayjs';

import { Button, ErrorResponse, Spinner, TooltipPortal } from '~components';
import { Select } from '~components/form-fields/inputs';
import Modal from '~components/Modal';
import { TableView } from '~components/table-view-v3';

import { ClientInvoiceStatusTransition } from '~lookups/models';

import PaymentSourceBadge from '~direct-debits/components/PaymentSourceBadge';

import { useCanManageInvoiceStatus } from '~support-at-home/hooks/useCanManageInvoiceStatus';

import { modalColumns } from '~support-at-home/forms/co-contributions/modalList';

import { useGetClientInvoiceStatusTransitionsQuery } from '~lookups/actions/clientInvoiceStatuses';
import {
  useGetClientInvoiceQuery,
  useUpdateClientInvoiceStatusMutation,
} from '~support-at-home/actions/clientInvoices';

import CoContributionExportActions from './CoContributionExportActions';

interface CoContributionsModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  clientId: string | null;
}

const SummaryRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="summary-row">
    <strong>{label}:</strong>
    <span>{value}</span>
  </div>
);

const CoContributionsModal: React.FC<CoContributionsModalProps> = ({ isOpen, setIsOpen, clientId }) => {
  const { data, isLoading, error: getError } = useGetClientInvoiceQuery({ id: clientId ?? '' }, { skip: !clientId });
  const { data: transitions = [] } = useGetClientInvoiceStatusTransitionsQuery();
  const [updateInvoice, { isLoading: isUpdating, error: updateError }] = useUpdateClientInvoiceStatusMutation();
  const canManage = useCanManageInvoiceStatus();

  const {
    client,
    items = [],
    invoice_date,
    due_date,
    reference = 'N/A',
    status_id,
    status,
    payment_source,
    total_amount,
  } = data ?? {};
  const { full_name, send_to } = client ?? {};

  const isLocked = payment_source === 'gocardless';
  const canEditStatus = canManage && !isLocked;
  const lockedSelectId = 'co-contributions-modal-status-locked';

  const [statusId, setStatusId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStatusId(null);
    }
  }, [isOpen]);

  const getStatusTransitionOptions = () => {
    const mappedTransitions = transitions
      .filter((transition: ClientInvoiceStatusTransition) => transition.from_status_id === status_id)
      .map((transition: ClientInvoiceStatusTransition) => ({
        label: transition.to_status?.name,
        value: transition.to_status_id,
      }))
      .filter((obj, i, self) => self.findIndex((t) => t.value === obj.value) === i);

    return [{ label: status?.name, value: status_id } as { label: string; value: string }, ...mappedTransitions];
  };

  const handleSave = async () => {
    // Authoritative gate — the Save button is disabled when !canEditStatus,
    // but the handler must guard too so a keyboard submit (Enter on a focused
    // element) or future external invocation can't bypass the lock/permission.
    if (!canEditStatus) return;
    if (!clientId || !statusId) return;
    const { error } = await updateInvoice({ id: clientId, status_id: statusId });
    if (!error) setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      setIsOpen={setIsOpen}
      header={`Edit Co-Contribution Invoice - ${reference}`}
      headerClassName="header"
      size="xl"
      footer={
        <div className="d-flex justify-content-between align-items-center w-100">
          <CoContributionExportActions row={clientId ? { id: clientId } : undefined} />
          <div className="d-flex gap-2">
            <Button color="success" disabled={!canEditStatus || !statusId || isUpdating} onClick={handleSave}>
              {isUpdating ? <Spinner loading size={5} /> : 'Save'}
            </Button>
            <Button color="secondary" disabled={isUpdating} onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      }
      footerClassName="d-flex"
    >
      <ErrorResponse message={getError?.message || updateError?.message} />
      {isLoading && (
        <div>
          <Spinner loading />
        </div>
      )}
      {data && (
        <div className="cocontributions-modal">
          {/* Summary Section */}
          <div className="summary">
            <div>
              <div className="mb-1">
                <strong>{full_name}</strong>
              </div>
              <div className="client-address">{send_to}</div>
            </div>
            <div className="details">
              <SummaryRow label="Reference" value={reference ?? 'N/A'} />
              <SummaryRow label="Invoice Date" value={invoice_date ? formatDateString(invoice_date) : 'N/A'} />
              <SummaryRow label="Due Date" value={due_date ? formatDateString(due_date) : 'N/A'} />
              <SummaryRow
                label="Status"
                value={
                  <div className="d-flex align-items-center gap-2">
                    {canEditStatus ? (
                      <Select
                        id="status-select"
                        name="status-select"
                        type="select"
                        value={statusId ?? status_id}
                        onChange={(_, val) => setStatusId(val as string)}
                        gateOptions={{ getValidOptions: getStatusTransitionOptions }}
                        className="select select-sm"
                        clearable={false}
                      />
                    ) : (
                      <>
                        <span
                          id={lockedSelectId}
                          data-cy="co-contributions-modal-status-readonly"
                          data-testid="co-contributions-modal-status-readonly"
                        >
                          {status?.name ?? '—'}
                        </span>
                        {isLocked ? (
                          <TooltipPortal
                            targetId={lockedSelectId}
                            content="Status set by GoCardless and cannot be manually changed"
                          />
                        ) : (
                          // Non-admin viewing an unlocked invoice — explain the
                          // read-only state with a permission-specific tooltip.
                          <TooltipPortal
                            targetId={lockedSelectId}
                            content="You do not have permission to change invoice statuses"
                          />
                        )}
                      </>
                    )}
                    <PaymentSourceBadge payment_source={payment_source} id={clientId ?? 'modal'} />
                  </div>
                }
              />
            </div>
          </div>

          {/* Invoice Items Table */}
          <TableView id="co-contributions-modal-table" columns={modalColumns} data={items} readonly striped />

          {/* Invoice Total */}
          <div className="totals">
            <div className="totals-container">
              <hr className="my-2" />
              <div className="amount-row">
                <strong>Invoice Total:</strong>
                <span>${total_amount?.toFixed(2) ?? '0.00'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CoContributionsModal;
